//@flow

import type {
  ActionType,
  TriggerFilterType,
  TriggerType,
} from '../../../../entities';
import {DEFAULT_EXECUTION_HOUR_SLOT} from '../../constants/executionTime';

/**
 * Single source of truth for trigger type API values.
 * Prefer these constants (and helpers below) over string literals.
 */
export const TRIGGER_TYPES: {|
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  CUSTOMER_INACTIVITY: 'CUSTOMER_INACTIVITY',
|} = Object.freeze({
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  CUSTOMER_INACTIVITY: 'CUSTOMER_INACTIVITY',
});

/**
 * Single source of truth for trigger filter type API keys.
 */
export const FILTER_TYPES: {|
  TRIGGER_TEXT_SEARCH: 'trigger_text_search',
  CUSTOMER_INACTIVITY: 'customer_inactivity',
  CRM_SEGMENT: 'crm_segment',
  CRM_TAGS: 'crm_tags',
  CRM_FUNNEL: 'crm_funnel',
|} = Object.freeze({
  TRIGGER_TEXT_SEARCH: 'trigger_text_search',
  CUSTOMER_INACTIVITY: 'customer_inactivity',
  CRM_SEGMENT: 'crm_segment',
  CRM_TAGS: 'crm_tags',
  CRM_FUNNEL: 'crm_funnel',
});

export const DEFAULT_TRIGGER_TYPE: TriggerType = TRIGGER_TYPES.MESSAGE_RECEIVED;

export function isCustomerInactivityTrigger(
  triggerType: ?TriggerType | ''
): boolean {
  return triggerType === TRIGGER_TYPES.CUSTOMER_INACTIVITY;
}

export function isMessageReceivedTrigger(
  triggerType: ?TriggerType | ''
): boolean {
  return triggerType === TRIGGER_TYPES.MESSAGE_RECEIVED;
}

export const MAX_CUSTOMER_INACTIVITY_DAYS = 7;

export const CUSTOMER_INACTIVITY_DAY_OPTIONS = Array.from(
  {length: MAX_CUSTOMER_INACTIVITY_DAYS},
  (_, index) => index + 1
);

/**
 * Day chips for the inactivity filter. Production default is 1–7.
 * Include 0 when `enable_whatsapp_web_provider` is on, or when the hydrated
 * value is already 0 so existing test triggers remain editable without the flag.
 */
export function getCustomerInactivityDayOptions({
  includeDayZero = false,
  currentInactiveDays,
}: {|
  includeDayZero?: boolean,
  currentInactiveDays?: ?number,
|} = {}): number[] {
  if (includeDayZero || currentInactiveDays === 0) {
    return [0, ...CUSTOMER_INACTIVITY_DAY_OPTIONS];
  }
  return CUSTOMER_INACTIVITY_DAY_OPTIONS;
}

export const ACTIVE_FILTER_TYPES: TriggerFilterType[] = [
  FILTER_TYPES.TRIGGER_TEXT_SEARCH,
  FILTER_TYPES.CUSTOMER_INACTIVITY,
  FILTER_TYPES.CRM_TAGS,
  FILTER_TYPES.CRM_FUNNEL,
];

export const TRIGGER_TYPE_CONFIG = {
  [TRIGGER_TYPES.MESSAGE_RECEIVED]: {
    label: 'Mensaje recibido',
    defaultFilters: [FILTER_TYPES.TRIGGER_TEXT_SEARCH],
    allowedFilters: [FILTER_TYPES.TRIGGER_TEXT_SEARCH],
    supportsMultipleFilters: false,
    actionType: 'SEND_MESSAGE',
  },
  [TRIGGER_TYPES.CUSTOMER_INACTIVITY]: {
    label: 'X días desde que el cliente no ha respondido',
    defaultFilters: [FILTER_TYPES.CUSTOMER_INACTIVITY],
    // Funnel + conversation-tag filters only apply to inactivity triggers.
    allowedFilters: [
      FILTER_TYPES.CUSTOMER_INACTIVITY,
      FILTER_TYPES.TRIGGER_TEXT_SEARCH,
      FILTER_TYPES.CRM_TAGS,
      FILTER_TYPES.CRM_FUNNEL,
    ],
    supportsMultipleFilters: true,
    actionType: 'SEND_INIT_MESSAGE',
  },
};

export const TRIGGER_FILTER_LABELS: {[TriggerFilterType]: string} = {
  [FILTER_TYPES.TRIGGER_TEXT_SEARCH]: 'Búsqueda de texto',
  [FILTER_TYPES.CUSTOMER_INACTIVITY]: 'Inactividad del cliente',
  [FILTER_TYPES.CRM_SEGMENT]: 'Segmento CRM',
  [FILTER_TYPES.CRM_TAGS]: 'Etiquetas de conversación',
  [FILTER_TYPES.CRM_FUNNEL]: 'Embudo',
};

export function getDefaultFiltersForTriggerType(
  triggerType: TriggerType
): TriggerFilterType[] {
  return TRIGGER_TYPE_CONFIG[triggerType].defaultFilters.filter(filterType =>
    ACTIVE_FILTER_TYPES.includes(filterType)
  );
}

export function getActionTypeForTriggerType(
  triggerType: TriggerType
): ActionType {
  return TRIGGER_TYPE_CONFIG[triggerType].actionType;
}

export function getAllowedFiltersForTriggerType(
  triggerType: TriggerType
): TriggerFilterType[] {
  return TRIGGER_TYPE_CONFIG[triggerType].allowedFilters.filter(filterType =>
    ACTIVE_FILTER_TYPES.includes(filterType)
  );
}

export function supportsMultipleFilters(triggerType: TriggerType): boolean {
  return TRIGGER_TYPE_CONFIG[triggerType].supportsMultipleFilters;
}

export function isDefaultFilter(
  triggerType: TriggerType,
  filterType: TriggerFilterType
): boolean {
  return getDefaultFiltersForTriggerType(triggerType).includes(filterType);
}

export function getDefaultFilterValues(filterType: TriggerFilterType): any {
  switch (filterType) {
    case FILTER_TYPES.TRIGGER_TEXT_SEARCH:
      return {
        searchText: '',
        searchTextPattern: 'MATCHES_WITH',
        enabledRelatedTexts: false,
        relatedSearchTexts: [],
      };
    case FILTER_TYPES.CUSTOMER_INACTIVITY:
      return {
        inactiveDays: 7,
      };
    case FILTER_TYPES.CRM_SEGMENT:
      return {
        segmentId: '',
      };
    case FILTER_TYPES.CRM_TAGS:
      return {
        tagIds: [],
      };
    case FILTER_TYPES.CRM_FUNNEL:
      return {
        funnelId: '',
        funnelLaneId: '',
      };
    default:
      return {};
  }
}

export function getDefaultExecutionTimeSlot(): string {
  return DEFAULT_EXECUTION_HOUR_SLOT;
}

/**
 * Fresh default filters only (defaults first). Used when the user switches
 * trigger type — previous filter values are discarded.
 */
export function applyTriggerTypeChange(
  triggerType: TriggerType
): {[key: TriggerFilterType]: any} {
  const nextFilters = {};

  getDefaultFiltersForTriggerType(triggerType).forEach(filterType => {
    nextFilters[filterType] = getDefaultFilterValues(filterType);
  });

  return nextFilters;
}

/**
 * Merge loaded/API filters for a trigger type: keep allowed values, ensure
 * default filters exist, and put defaults first in key order.
 */
export function mergeFiltersForTriggerType(
  triggerType: TriggerType,
  currentFilters: {[key: TriggerFilterType]: any} = {}
): {[key: TriggerFilterType]: any} {
  const allowedFilters = getAllowedFiltersForTriggerType(triggerType);
  const defaultFilters = getDefaultFiltersForTriggerType(triggerType);
  const nextFilters = {};

  defaultFilters.forEach(filterType => {
    nextFilters[filterType] = currentFilters[filterType]
      ? currentFilters[filterType]
      : getDefaultFilterValues(filterType);
  });

  allowedFilters.forEach(filterType => {
    if (nextFilters[filterType] != null) return;
    if (currentFilters[filterType] != null) {
      nextFilters[filterType] = currentFilters[filterType];
    }
  });

  return nextFilters;
}

export function orderActiveFilterTypes(
  triggerType: TriggerType,
  filterTypes: TriggerFilterType[]
): TriggerFilterType[] {
  const defaults = getDefaultFiltersForTriggerType(triggerType);
  const defaultSet = new Set(defaults);
  const orderedDefaults = defaults.filter(type => filterTypes.includes(type));
  const rest = filterTypes.filter(type => !defaultSet.has(type));

  return [...orderedDefaults, ...rest];
}
